import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable, Button, PageHeader } from '../../../components';
import { residencesService } from '../../../services';
import { AlertCircle, RefreshCw, ClipboardList, Inbox } from 'lucide-react';

const EnquiryList = () => {
    const navigate = useNavigate();
    const [enquiries, setEnquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);

    useEffect(() => {
        fetchEnquiries();
    }, []);

    const fetchEnquiries = async () => {
        setLoading(true);
        try {
            const response = await residencesService.getAllEnquiries();
            if (response.success) {
                setEnquiries(response.data || []);
                setError('');
            } else {
                setError(response.message || 'Failed to fetch enquiries');
            }
        } catch (err) {
            console.error('Error fetching enquiries:', err);
            setError(err.message || 'Error occurred while fetching enquiries');
        } finally {
            setLoading(false);
        }
    };

    const handleConvert = () => {
        navigate('/manager/enquiries/convert', { state: { selectedIds } });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch (e) {
            return dateStr;
        }
    };

    // Table columns
    const columns = [
        {
            key: 'id', header: 'Enquiry ID', sortable: true, sortAccessor: (e) => e.id,
            render: (e) => <span className="font-semibold text-[#475569] dark:text-gray-300 tabular-nums">#{String(e.id).padStart(4, '0')}</span>,
        },
        {
            key: 'client_name', header: 'Client Name', sortable: true,
            render: (e) => <span className="font-bold text-[#0B1220] dark:text-gray-100">{e.client_name}</span>,
        },
        {
            key: 'enquirer_name', header: 'Enquirer Name', sortable: true,
            render: (e) => <span className="font-semibold text-[#334155] dark:text-gray-300">{e.enquirer_name}</span>,
        },
        { key: 'contact_number', header: 'Contact No', render: (e) => e.contact_number || '—' },
        { key: 'address', header: 'Address', render: (e) => e.address || '—' },
        {
            key: 'enquiry_date', header: 'Enquire Date', sortable: true, sortAccessor: (e) => e.enquiry_date || '',
            render: (e) => <span className="tabular-nums">{formatDate(e.enquiry_date)}</span>,
        },
    ];

    return (
        <div className="w-full space-y-6 pb-8">

            {/* Header Section */}
            <PageHeader
                title="Enquiry List"
                subtitle={`${enquiries.length} enquiries found`}
                actions={(
                    <>
                        <Button
                            variant="secondary"
                            icon={<RefreshCw className={`w-[18px] h-[18px] ${loading ? 'animate-spin' : ''}`} />}
                            onClick={fetchEnquiries}
                            title="Refresh"
                            aria-label="Refresh"
                        />
                        {/* New enquiries start in Enquiries & Assessments; people arrive
                            here once their assessment is approved and planned. */}
                        <Button variant="primary" leftIcon={<ClipboardList className="w-4 h-4" />} onClick={() => navigate('/manager/enquiries-assessments')}>
                            Enquiries &amp; Assessments
                        </Button>
                    </>
                )}
            />

            {/* Error Alert */}
            {error && (
                <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-900/50 rounded-xl p-4 flex gap-3 text-red-700 dark:text-red-400">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm font-medium">{error}</p>
                </div>
            )}

            {/* Enquiries table */}
            <DataTable
                columns={columns}
                data={enquiries}
                loading={loading}
                rowKey={(e) => e.id}
                selectable
                onSelectionChange={(rows) => setSelectedIds(rows.map((r) => r.id))}
                renderBulkActions={(rows) => (
                    <Button size="sm" variant="primary" onClick={handleConvert}>
                        Convert ({rows.length}) to Client
                    </Button>
                )}
                emptyIcon={Inbox}
                emptyTitle="No enquiries found"
                emptyText="Approved and planned enquiries from Enquiries & Assessments appear here."
            />

        </div>
    );
};

export default EnquiryList;
